import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input, Label } from "@/components/ui/input";
import {
  addKpiLog,
  deleteKpiLog,
  derivedValueFromLogs,
  listKpiLogs,
  type KpiLog,
} from "@/features/kpi/logs";
import { periodEndExclusive, upsertKpiEntry, type KpiEntry, type KpiPeriodType } from "@/features/kpi/repo";

function logCopy(kpiId: string): { label: string; amount: boolean; hint: string } {
  if (kpiId === "new-direct-customers") {
    return {
      label: "Customer name",
      amount: false,
      hint: "Each row counts as one new direct account for this period.",
    };
  }
  if (kpiId === "pipeline-value") {
    return {
      label: "Opportunity",
      amount: true,
      hint: "The period value is the sum of amounts logged here.",
    };
  }
  if (kpiId === "customer-service-incidents") {
    return {
      label: "Incident",
      amount: false,
      hint: "Log the event here. Type the incident rate on the row — it still needs load count.",
    };
  }
  if (kpiId === "preventable-accidents") {
    return {
      label: "Event",
      amount: false,
      hint: "Log reviewed events here. Type the rate per million miles on the row.",
    };
  }
  if (kpiId === "training-completion") {
    return {
      label: "Course / person",
      amount: false,
      hint: "Log completions here. Type the period completion % on the row.",
    };
  }
  return { label: "Label", amount: false, hint: "Log events for this KPI." };
}

function defaultOccurredOn(periodStart: string, endExclusive: string): string {
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  if (today >= periodStart && today < endExclusive) return today;
  return periodStart;
}

function inclusiveEnd(endExclusive: string): string {
  const [year, month, day] = endExclusive.split("-").map(Number);
  const date = new Date(year, month - 1, day - 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function KpiLogPanel({
  kpiId,
  periodType,
  periodStart,
  currentEntry,
  userId,
  readOnly = false,
  onDerivedChange,
}: {
  kpiId: string;
  periodType: KpiPeriodType;
  periodStart: string;
  currentEntry?: KpiEntry;
  userId?: string;
  readOnly?: boolean;
  onDerivedChange?: () => void;
}) {
  const copy = logCopy(kpiId);
  const endExclusive = periodEndExclusive(periodStart, periodType);
  const [logs, setLogs] = useState<KpiLog[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState({
    occurredOn: defaultOccurredOn(periodStart, endExclusive),
    label: "",
    note: "",
    amount: "",
  });

  const refresh = useCallback(async () => {
    try {
      setLogs(await listKpiLogs(kpiId, periodStart, endExclusive));
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not load the event log.");
    }
  }, [endExclusive, kpiId, periodStart]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    setDraft((current) => ({
      ...current,
      occurredOn: defaultOccurredOn(periodStart, endExclusive),
    }));
  }, [endExclusive, periodStart]);

  async function syncDerived(nextLogs: KpiLog[]) {
    const derived = derivedValueFromLogs(kpiId, nextLogs);
    if (derived === null) return;
    if (currentEntry?.source === "auto") return;
    await upsertKpiEntry(
      {
        kpiId,
        periodType,
        periodStart,
        value: derived,
        note: currentEntry?.note || "From event log",
      },
      userId,
    );
    onDerivedChange?.();
  }

  async function add() {
    if (!draft.label.trim()) return;
    const amount = copy.amount && draft.amount.trim() !== "" ? Number(draft.amount) : null;
    if (copy.amount && (amount === null || !Number.isFinite(amount))) {
      setError("Enter a dollar amount.");
      return;
    }
    try {
      const occurredOn =
        draft.occurredOn >= periodStart && draft.occurredOn < endExclusive
          ? draft.occurredOn
          : periodStart;
      await addKpiLog(
        {
          kpiId,
          occurredOn,
          label: draft.label.trim(),
          note: draft.note.trim(),
          amount,
        },
        userId,
      );
      setDraft({
        occurredOn: defaultOccurredOn(periodStart, endExclusive),
        label: "",
        note: "",
        amount: "",
      });
      const next = await listKpiLogs(kpiId, periodStart, endExclusive);
      setLogs(next);
      await syncDerived(next);
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not save the event.");
    }
  }

  async function remove(id: string) {
    try {
      await deleteKpiLog(id);
      const next = await listKpiLogs(kpiId, periodStart, endExclusive);
      setLogs(next);
      await syncDerived(next);
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not delete the event.");
    }
  }

  return (
    <div className="mt-4 rounded-md border border-brand-200 bg-white p-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-brand-400">Event log</div>
      <p className="mt-1 text-xs text-brand-500">{copy.hint}</p>
      {logs.length > 0 && (
        <div className="mt-3 grid gap-1.5">
          {logs.map((log) => (
            <div key={log.id} className="flex items-start gap-2 rounded border border-brand-100 px-2 py-1.5 text-sm">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-brand-900">
                  {log.label}
                  {log.amount !== null ? ` · $${log.amount.toLocaleString("en-US")}` : ""}
                </div>
                <div className="text-xs text-brand-400">
                  {log.occurredOn}{log.note ? ` · ${log.note}` : ""}
                </div>
              </div>
              {!readOnly && (
                <button
                  type="button"
                  aria-label={`Delete ${log.label}`}
                  onClick={() => void remove(log.id)}
                  className="rounded p-1 text-brand-400 hover:bg-brand-50 hover:text-red-600"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {!readOnly && (
        <div className="mt-3 grid gap-2 md:grid-cols-[8rem_1fr_auto] lg:grid-cols-[8rem_1fr_6rem_1fr_auto] lg:items-end">
          <div>
            <Label htmlFor={`${kpiId}-log-date`}>Date</Label>
            <Input
              id={`${kpiId}-log-date`}
              type="date"
              min={periodStart}
              max={inclusiveEnd(endExclusive)}
              value={draft.occurredOn}
              onChange={(event) => setDraft({ ...draft, occurredOn: event.target.value })}
            />
          </div>
          <div>
            <Label htmlFor={`${kpiId}-log-label`}>{copy.label}</Label>
            <Input
              id={`${kpiId}-log-label`}
              value={draft.label}
              onChange={(event) => setDraft({ ...draft, label: event.target.value })}
            />
          </div>
          {copy.amount && (
            <div>
              <Label htmlFor={`${kpiId}-log-amount`}>Amount</Label>
              <Input
                id={`${kpiId}-log-amount`}
                type="number"
                step="any"
                inputMode="decimal"
                value={draft.amount}
                onChange={(event) => setDraft({ ...draft, amount: event.target.value })}
              />
            </div>
          )}
          <div>
            <Label htmlFor={`${kpiId}-log-note`}>Note</Label>
            <Input
              id={`${kpiId}-log-note`}
              value={draft.note}
              onChange={(event) => setDraft({ ...draft, note: event.target.value })}
            />
          </div>
          <Button size="sm" onClick={() => void add()} disabled={!draft.label.trim()}>
            <Plus className="mr-1.5 h-4 w-4" /> Add
          </Button>
        </div>
      )}
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </div>
  );
}
